import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { ContactForm } from "@/components/ContactForm";
import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { useTranslation } from "react-i18next";
import headerImage from "@/assets/headers/header-contato.jpg";

const Contato = () => {
  const { t } = useTranslation();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
      "@type": "ReligiousOrganization",
      "name": "TUPJA - Terreiro Pai Joaquim de Aruanda",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "316 Goodland St",
        "addressLocality": "Orlando",
        "addressRegion": "FL",
        "postalCode": "32805",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "28.527787",
        "longitude": "-81.367402"
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Friday",
        "opens": "19:30",
        "closes": "23:30"
      },
      "email": "contato@tupja.com"
    }
  };

  return (
    <Layout>
      <SEO 
        title={t('contact.title')}
        description={t('contact.subtitle')}
        keywords="contato terreiro orlando, endereço tupja, localização umbanda orlando, como chegar terreiro, horário giras orlando"
        canonicalUrl={`${window.location.origin}/contato`}
        structuredData={structuredData}
      />
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${headerImage})` }}
        />
        <div className="absolute inset-0 bg-logo-brown-dark/70" />
        <div className="relative z-10 text-center px-4 container mx-auto">
          <h1 className="font-display text-5xl md:text-7xl text-logo-beige mb-6 font-bold">
            {t('contact.title')}
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto font-body">
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Left Column - Contact Details */}
            <div className="space-y-8">
              <div>
                <div className="flex items-start gap-4 mb-6">
                  <MapPin className="w-6 h-6 text-sand-dark flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-display text-2xl text-sand-dark mb-2">{t('contact.address')}</h3>
                    <a href="https://www.google.com/maps/search/?api=1&query=316+Goodland+St+Orlando+FL" target="_blank" rel="noopener noreferrer" className="text-foreground/80 hover:text-primary hover:underline transition-colors leading-relaxed block">
                      316 Goodland St<br />
                      Orlando, FL
                    </a>
                  </div>
                </div>
              </div>

              <div className="h-px bg-sand-dark/20" />

              <div>
                <div className="flex items-start gap-4 mb-6">
                  <Clock className="w-6 h-6 text-sand-dark flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-display text-2xl text-sand-dark mb-2">{t('contact.schedule')}</h3>
                    <p className="text-foreground/80 leading-relaxed">
                      {t('contact.scheduleDay')}<br />
                      {t('contact.scheduleTime')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="h-px bg-sand-dark/20" />

              <div>
                <div className="flex items-start gap-4 mb-6">
                  <Phone className="w-6 h-6 text-sand-dark flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-display text-2xl text-sand-dark mb-2">{t('contact.contactLabel')}</h3>
                    <p className="text-foreground/80 leading-relaxed">
                      WhatsApp: <a href="https://chat.whatsapp.com/HHikWVW5MG168CoHyqNtzL?mode=hqrt1" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline transition-colors">{t('contact.whatsappLink')}</a><br />
                      Instagram: <a href="https://www.instagram.com/terreiropaijoaquimdearuanda/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline transition-colors">{t('contact.instagramLink')}</a>
                    </p>
                  </div>
                </div>
              </div>

              <div className="h-px bg-sand-dark/20" />

              <div>
                <div className="flex items-start gap-4">
                  <Mail className="w-6 h-6 text-sand-dark flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-display text-2xl text-sand-dark mb-2">{t('contact.email')}</h3>
                    <a href="mailto:contato@tupja.com" className="text-foreground/80 hover:text-primary hover:underline transition-colors leading-relaxed block">
                      contato@tupja.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Contact Form */}
            <div>
              <h3 className="font-display text-3xl text-sand-dark mb-6">{t('contact.contactUs')}</h3>
              <ContactForm />
            </div>
          </div>

          {/* Map Section */}
          <div className="mt-16 max-w-6xl mx-auto">
            <div className="bg-sand-light/30 rounded-lg overflow-hidden" style={{ height: '400px' }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3504.5257872847893!2d-81.37985492464795!3d28.54722837572076!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e77e7d3e8f8f8f%3A0x8f8f8f8f8f8f8f8f!2s316%20Goodland%20St%2C%20Orlando%2C%20FL%2032805!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localização do TUPJA"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
    </Layout>
  );
};

export default Contato;